import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Chip,
  InputAdornment,
  CircularProgress,
  alpha,
  Divider,
  Tabs,
  Tab,
  Badge,
  Stack,
  useMediaQuery,
  useTheme,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  DirectionsCar as CarIcon,
  People as PeopleIcon,
  ReceiptLong as SalesIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  Settings as SettingsIcon,
  Dashboard as DashboardIcon,
  Assessment as ReportsIcon,
  Psychology as IntelligenceIcon,
  AutoAwesome as AIIcon,
  Campaign as AdvertisingIcon,
  Assignment as TasksIcon,
  AccountBalance as AccountingIcon,
  CalendarMonth as CalendarIcon,
  CheckCircle as TaskIcon,
  Construction as ServiceIcon,
  ShoppingCart as PurchaseOrderIcon,
  MonetizationOn as ExpenseIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { salesService } from '../services/sales.service';

interface SearchResult {
  id: string;
  type: 'vehicle' | 'customer' | 'sale' | 'module' | 'recent' | 'task' | 'service' | 'purchase-order' | 'expense';
  title: string;
  subtitle: string;
  path: string;
  metadata?: string;
}

interface GlobalSearchModalProps {
  open: boolean;
  onClose: () => void;
}

type FilterType = 'all' | 'vehicle' | 'customer' | 'sale' | 'module' | 'task' | 'service' | 'purchase-order' | 'expense';

interface RecentSearch {
  query: string;
  timestamp: number;
}

// Module/Page definitions for quick navigation
const MODULES = [
  { name: 'Dashboard', path: '/', icon: DashboardIcon, keywords: ['home', 'dashboard', 'overview'] },
  { name: 'Inventory', path: '/inventory', icon: CarIcon, keywords: ['vehicles', 'stock', 'cars', 'inventory'] },
  { name: 'Customers', path: '/customers', icon: PeopleIcon, keywords: ['customers', 'clients', 'leads'] },
  { name: 'Sales', path: '/sales', icon: SalesIcon, keywords: ['sales', 'deals', 'quotes'] },
  { name: 'Reports', path: '/reports', icon: ReportsIcon, keywords: ['reports', 'analytics', 'stats'] },
  { name: 'Intelligence Hub', path: '/intelligence', icon: IntelligenceIcon, keywords: ['intelligence', 'insights', 'analytics'] },
  { name: 'AI Assistant', path: '/ai', icon: AIIcon, keywords: ['ai', 'assistant', 'chat', 'help'] },
  { name: 'Settings', path: '/settings', icon: SettingsIcon, keywords: ['settings', 'preferences', 'config'] },
  { name: 'Advertising', path: '/advertising', icon: AdvertisingIcon, keywords: ['advertising', 'marketing', 'ads'] },
  { name: 'Tasks', path: '/tasks', icon: TasksIcon, keywords: ['tasks', 'todos', 'assignments'] },
  { name: 'Accounting', path: '/accounting', icon: AccountingIcon, keywords: ['accounting', 'xero', 'finance'] },
  { name: 'Calendar', path: '/calendar', icon: CalendarIcon, keywords: ['calendar', 'schedule', 'appointments'] },
];

export default function GlobalSearchModal({ open, onClose }: GlobalSearchModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [filterTab, setFilterTab] = useState<FilterType>('all');
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([]);
  const navigate = useNavigate();
  const { currentOrganization } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('recentSearches');
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load recent searches:', e);
      }
    }
  }, []);

  // Save recent search
  const saveRecentSearch = (query: string) => {
    if (!query.trim()) return;

    const newRecentSearches = [
      { query: query.trim(), timestamp: Date.now() },
      ...recentSearches.filter(s => s.query !== query.trim())
    ].slice(0, 10); // Keep only last 10 searches

    setRecentSearches(newRecentSearches);
    localStorage.setItem('recentSearches', JSON.stringify(newRecentSearches));
  };

  // Search function with debouncing
  const performSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    setLoading(true);
    const searchResults: SearchResult[] = [];

    try {
      const searchTerm = `%${query.toLowerCase()}%`;
      const queryLower = query.toLowerCase();
      console.log('🔍 Searching for:', query, 'Organization:', currentOrganization?.id);

      // Search Modules/Pages first (instant, no API call needed)
      MODULES.forEach((module) => {
        const nameMatch = module.name.toLowerCase().includes(queryLower);
        const keywordMatch = module.keywords.some(k => k.includes(queryLower));

        if (nameMatch || keywordMatch) {
          searchResults.push({
            id: module.path,
            type: 'module',
            title: module.name,
            subtitle: 'Navigate to page',
            path: module.path,
            metadata: module.keywords.join(', '),
          });
        }
      });

      if (!currentOrganization?.id) {
        setResults(searchResults);
        setLoading(false);
        return;
      }

      // Search Vehicles - comprehensive word-based search
      const searchQuery = query.trim();
      const words = searchQuery.toLowerCase().split(/\s+/).filter(w => w.length > 0);
      const vehicleSearchTerms: string[] = [];

      // Search each word in make/model/trim
      words.forEach(word => {
        if (!/^(19|20)\d{2}$/.test(word)) { // Skip years
          vehicleSearchTerms.push(`make.ilike.%${word}%`);
          vehicleSearchTerms.push(`model.ilike.%${word}%`);
          vehicleSearchTerms.push(`trim.ilike.%${word}%`);
        }
      });

      // Also search full query
      vehicleSearchTerms.push(`make.ilike.%${searchQuery}%`);
      vehicleSearchTerms.push(`model.ilike.%${searchQuery}%`);
      vehicleSearchTerms.push(`vin.ilike.%${searchQuery}%`);
      vehicleSearchTerms.push(`registration.ilike.%${searchQuery}%`);
      vehicleSearchTerms.push(`stock_number.ilike.%${searchQuery}%`);

      const { data: vehicles, error: vehiclesError } = await supabase
        .from('vehicles')
        .select('id, stock_number, make, model, year, vin, registration')
        .eq('organization_id', currentOrganization.id)
        .or(vehicleSearchTerms.join(','))
        .limit(10);

      if (vehiclesError) {
        console.error('❌ Vehicles search error:', vehiclesError);
      } else {
        console.log('✅ Vehicles found:', vehicles?.length || 0, vehicles);
      }

      if (!vehiclesError && vehicles) {
        vehicles.forEach((vehicle) => {
          searchResults.push({
            id: vehicle.id,
            type: 'vehicle',
            title: `${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''}`.trim() || 'Unknown Vehicle',
            subtitle: vehicle.stock_number || 'No stock number',
            path: `/inventory/${vehicle.id}`,
            metadata: vehicle.vin || vehicle.registration || '',
          });
        });
      }

      // Search Customers - comprehensive word-based search
      const customerSearchTerms: string[] = [];

      // Search each word in first/last name
      words.forEach(word => {
        customerSearchTerms.push(`first_name.ilike.%${word}%`);
        customerSearchTerms.push(`last_name.ilike.%${word}%`);
      });

      // Also search full query
      customerSearchTerms.push(`first_name.ilike.%${searchQuery}%`);
      customerSearchTerms.push(`last_name.ilike.%${searchQuery}%`);
      customerSearchTerms.push(`email.ilike.%${searchQuery}%`);
      customerSearchTerms.push(`phone.ilike.%${searchQuery}%`);
      customerSearchTerms.push(`mobile.ilike.%${searchQuery}%`);

      const { data: customers, error: customersError } = await supabase
        .from('customers')
        .select('id, first_name, last_name, email, mobile, phone')
        .eq('organization_id', currentOrganization.id)
        .or(customerSearchTerms.join(','))
        .limit(10);

      if (customersError) {
        console.error('❌ Customers search error:', customersError);
      } else {
        console.log('✅ Customers found:', customers?.length || 0, customers);
      }

      if (!customersError && customers) {
        customers.forEach((customer) => {
          searchResults.push({
            id: customer.id,
            type: 'customer',
            title: `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Unnamed Customer',
            subtitle: customer.email || customer.mobile || customer.phone || 'No contact info',
            path: `/customers/${customer.id}`,
            metadata: customer.email || '',
          });
        });
      }

      // Search Deals - use salesService which now searches comprehensively
      const dealsResult = await salesService.getDeals({ search: query }, 1, 10);

      if (dealsResult.error) {
        console.error('❌ Deals search error:', dealsResult.error);
      } else if (dealsResult.data?.data) {
        const deals = dealsResult.data.data;
        console.log('✅ Deals found:', deals.length);

        deals.forEach((deal: any) => {
          const customerName = deal.customer
            ? `${deal.customer.first_name || ''} ${deal.customer.last_name || ''}`.trim()
            : 'Unknown Customer';

          const vehicleInfo = deal.vehicle
            ? `${deal.vehicle.year || ''} ${deal.vehicle.make || ''} ${deal.vehicle.model || ''}`.trim()
            : 'No vehicle';

          searchResults.push({
            id: deal.id,
            type: 'sale',
            title: `Sale #${deal.deal_number || deal.id.slice(0, 8)}`,
            subtitle: customerName || 'No customer',
            path: `/sales/${deal.id}`,
            metadata: vehicleInfo,
          });
        });
      }

      // Search Tasks
      const { data: tasks, error: tasksError } = await supabase
        .from('tasks')
        .select('id, title, description, due_date, status, priority')
        .eq('organization_id', currentOrganization.id)
        .or(`title.ilike.${searchTerm},description.ilike.${searchTerm}`)
        .limit(10);

      if (tasksError) {
        console.error('❌ Tasks search error:', tasksError);
      } else {
        console.log('✅ Tasks found:', tasks?.length || 0, tasks);
      }

      if (!tasksError && tasks) {
        tasks.forEach((task) => {
          searchResults.push({
            id: task.id,
            type: 'task',
            title: task.title || 'Untitled Task',
            subtitle: task.description || 'No description',
            path: `/tasks?task=${task.id}`,
            metadata: `${task.status || 'pending'} • ${task.priority || 'normal'} priority`,
          });
        });
      }

      // Search Service Records / Workshop Jobs
      const { data: serviceRecords, error: serviceError } = await supabase
        .from('service_records')
        .select(`
          id,
          service_type,
          description,
          status,
          vehicle:vehicles(make, model, year, stock_number)
        `)
        .eq('organization_id', currentOrganization.id)
        .or(`service_type.ilike.${searchTerm},description.ilike.${searchTerm}`)
        .limit(10);

      if (serviceError) {
        console.error('❌ Service records search error:', serviceError);
      } else {
        console.log('✅ Service records found:', serviceRecords?.length || 0, serviceRecords);
      }

      if (!serviceError && serviceRecords) {
        serviceRecords.forEach((service: any) => {
          const vehicleInfo = service.vehicle
            ? `${service.vehicle.year || ''} ${service.vehicle.make || ''} ${service.vehicle.model || ''}`.trim()
            : 'No vehicle';

          searchResults.push({
            id: service.id,
            type: 'service',
            title: service.service_type || 'Service Job',
            subtitle: vehicleInfo,
            path: `/workshop?service=${service.id}`,
            metadata: `${service.status || 'pending'} • ${service.description || ''}`,
          });
        });
      }

      // Search Purchase Orders
      const { data: purchaseOrders, error: poError } = await supabase
        .from('purchase_orders')
        .select('id, po_number, supplier_name, total_amount, status')
        .eq('organization_id', currentOrganization.id)
        .or(`po_number.ilike.${searchTerm},supplier_name.ilike.${searchTerm}`)
        .limit(10);

      if (poError) {
        console.error('❌ Purchase orders search error:', poError);
      } else {
        console.log('✅ Purchase orders found:', purchaseOrders?.length || 0, purchaseOrders);
      }

      if (!poError && purchaseOrders) {
        purchaseOrders.forEach((po) => {
          searchResults.push({
            id: po.id,
            type: 'purchase-order',
            title: `PO #${po.po_number || po.id.slice(0, 8)}`,
            subtitle: po.supplier_name || 'Unknown Supplier',
            path: `/purchase-orders/${po.id}`,
            metadata: `$${po.total_amount?.toFixed(2) || '0.00'} • ${po.status || 'pending'}`,
          });
        });
      }

      // Search Overhead Expenses
      const { data: expenses, error: expensesError } = await supabase
        .from('overhead_expenses')
        .select('id, description, amount, category, expense_date')
        .eq('organization_id', currentOrganization.id)
        .or(`description.ilike.${searchTerm},category.ilike.${searchTerm}`)
        .limit(10);

      if (expensesError) {
        console.error('❌ Expenses search error:', expensesError);
      } else {
        console.log('✅ Expenses found:', expenses?.length || 0, expenses);
      }

      if (!expensesError && expenses) {
        expenses.forEach((expense) => {
          searchResults.push({
            id: expense.id,
            type: 'expense',
            title: expense.description || 'Expense',
            subtitle: expense.category || 'Uncategorized',
            path: `/overhead-expenses?expense=${expense.id}`,
            metadata: `$${expense.amount?.toFixed(2) || '0.00'} • ${new Date(expense.expense_date).toLocaleDateString()}`,
          });
        });
      }

      console.log('📊 Total search results:', searchResults.length, searchResults);
      setResults(searchResults);
      setSelectedIndex(0);
    } catch (error) {
      console.error('❌ Search error:', error);
    } finally {
      setLoading(false);
    }
  }, [currentOrganization?.id]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      performSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, performSearch]);

  // Filter results based on selected tab
  const filteredResults = filterTab === 'all'
    ? results
    : results.filter(r => r.type === filterTab);

  // Count results by type
  const resultCounts = {
    all: results.length,
    vehicle: results.filter(r => r.type === 'vehicle').length,
    customer: results.filter(r => r.type === 'customer').length,
    sale: results.filter(r => r.type === 'sale').length,
    module: results.filter(r => r.type === 'module').length,
    task: results.filter(r => r.type === 'task').length,
    service: results.filter(r => r.type === 'service').length,
    'purchase-order': results.filter(r => r.type === 'purchase-order').length,
    expense: results.filter(r => r.type === 'expense').length,
  };

  // Reset when modal opens/closes
  useEffect(() => {
    if (open) {
      setSearchQuery('');
      setResults([]);
      setSelectedIndex(0);
      setFilterTab('all');
    }
  }, [open]);

  // Keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, filteredResults.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (event.key === 'Enter' && filteredResults[selectedIndex]) {
      event.preventDefault();
      handleResultClick(filteredResults[selectedIndex]);
    } else if (event.key === 'Escape') {
      onClose();
    }
  };

  const handleResultClick = (result: SearchResult) => {
    // Save to recent searches (except for recent search items themselves)
    if (result.type !== 'recent' && searchQuery.trim()) {
      saveRecentSearch(searchQuery);
    }
    navigate(result.path);
    onClose();
  };

  // Handle clicking a recent search
  const handleRecentSearchClick = (query: string) => {
    setSearchQuery(query);
    // This will trigger the search via useEffect
  };

  const getResultIcon = (type: string) => {
    switch (type) {
      case 'vehicle':
        return <CarIcon />;
      case 'customer':
        return <PeopleIcon />;
      case 'sale':
        return <SalesIcon />;
      case 'module':
        return <DashboardIcon />;
      case 'recent':
        return <HistoryIcon />;
      case 'task':
        return <TaskIcon />;
      case 'service':
        return <ServiceIcon />;
      case 'purchase-order':
        return <PurchaseOrderIcon />;
      case 'expense':
        return <ExpenseIcon />;
      default:
        return <SearchIcon />;
    }
  };

  const getResultColor = (type: string) => {
    switch (type) {
      case 'vehicle':
        return 'primary';
      case 'customer':
        return 'success';
      case 'sale':
        return 'warning';
      case 'module':
        return 'info';
      case 'task':
        return 'secondary';
      case 'service':
        return 'error';
      case 'purchase-order':
        return 'primary';
      case 'expense':
        return 'warning';
      case 'recent':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          borderRadius: isMobile ? 0 : 2,
          maxHeight: isMobile ? '100vh' : '80vh',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Search Input */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
          {isMobile && (
            <IconButton onClick={onClose} edge="start">
              <ArrowBackIcon />
            </IconButton>
          )}
          <TextField
            fullWidth
            autoFocus
            placeholder={isMobile ? "Search..." : "Search anything... vehicles, customers, sales, tasks, service jobs, expenses, purchase orders, pages..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            InputProps={{
              startAdornment: !isMobile ? (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ) : null,
              endAdornment: (
                <InputAdornment position="end">
                  {loading ? (
                    <CircularProgress size={20} />
                  ) : searchQuery ? (
                    <IconButton
                      onClick={() => setSearchQuery('')}
                      edge="end"
                      size="small"
                      sx={{ visibility: searchQuery ? 'visible' : 'hidden' }}
                    >
                      <CloseIcon />
                    </IconButton>
                  ) : null}
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: alpha('#fff', 0.05),
                '& fieldset': {
                  border: 'none',
                },
              },
            }}
          />
        </Box>

        {/* Filter Tabs - Desktop Only */}
        {!isMobile && searchQuery.trim() && results.length > 0 && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={filterTab}
              onChange={(_, newValue) => {
                setFilterTab(newValue);
                setSelectedIndex(0);
              }}
              variant="fullWidth"
              sx={{
                minHeight: 48,
                '& .MuiTab-root': {
                  minHeight: 48,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  '&:focus': {
                    outline: 'none'
                  },
                  '&:focus-visible': {
                    outline: 'none'
                  }
                },
              }}
            >
              <Tab
                label={
                  <Badge badgeContent={resultCounts.all} color="primary" max={99}>
                    <Box sx={{ px: 1 }}>All</Box>
                  </Badge>
                }
                value="all"
              />
              <Tab
                label={
                  <Badge badgeContent={resultCounts.vehicle} color="primary" max={99}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CarIcon fontSize="small" />
                      <span>Vehicles</span>
                    </Box>
                  </Badge>
                }
                value="vehicle"
              />
              <Tab
                label={
                  <Badge badgeContent={resultCounts.customer} color="primary" max={99}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PeopleIcon fontSize="small" />
                      <span>Customers</span>
                    </Box>
                  </Badge>
                }
                value="customer"
              />
              <Tab
                label={
                  <Badge badgeContent={resultCounts.sale} color="primary" max={99}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <SalesIcon fontSize="small" />
                      <span>Sales</span>
                    </Box>
                  </Badge>
                }
                value="sale"
              />
            </Tabs>
          </Box>
        )}

        {/* Results List */}
        <Box sx={{ maxHeight: 'calc(80vh - 150px)', overflow: 'auto' }}>
          {!searchQuery.trim() ? (
            <Box sx={{ p: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, px: 1 }}>
                Quick Navigation
              </Typography>
              <List dense>
                {MODULES.slice(0, 6).map((module) => {
                  const Icon = module.icon;
                  return (
                    <ListItemButton
                      key={module.path}
                      onClick={() => {
                        navigate(module.path);
                        onClose();
                      }}
                      sx={{ borderRadius: 1, mb: 0.5 }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Icon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={module.name}
                        secondary={module.keywords[0]}
                        secondaryTypographyProps={{ fontSize: '0.75rem' }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>

              {recentSearches.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, px: 1 }}>
                    Recent Searches
                  </Typography>
                  <List dense>
                    {recentSearches.slice(0, 5).map((search, index) => (
                      <ListItemButton
                        key={index}
                        onClick={() => handleRecentSearchClick(search.query)}
                        sx={{ borderRadius: 1, mb: 0.5 }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <HistoryIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={search.query}
                          secondary="Click to search again"
                          secondaryTypographyProps={{ fontSize: '0.75rem' }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </>
              )}

            </Box>
          ) : filteredResults.length === 0 && !loading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {filterTab === 'all'
                  ? `No results found for "${searchQuery}"`
                  : `No ${filterTab}s found for "${searchQuery}"`
                }
              </Typography>
            </Box>
          ) : isMobile ? (
            // Mobile: Vertical sections
            <Box sx={{ py: 1 }}>
              {['vehicle', 'customer', 'sale', 'task', 'service', 'purchase-order', 'expense', 'module'].map((type) => {
                const typeResults = results.filter((r) => r.type === type);
                if (typeResults.length === 0) return null;

                const typeLabels = {
                  vehicle: { label: 'Vehicles', icon: CarIcon },
                  customer: { label: 'Customers', icon: PeopleIcon },
                  sale: { label: 'Sales', icon: SalesIcon },
                  task: { label: 'Tasks', icon: TaskIcon },
                  service: { label: 'Service Jobs', icon: ServiceIcon },
                  'purchase-order': { label: 'Purchase Orders', icon: PurchaseOrderIcon },
                  expense: { label: 'Expenses', icon: ExpenseIcon },
                  module: { label: 'Pages', icon: DashboardIcon },
                };

                const TypeIcon = typeLabels[type as keyof typeof typeLabels]?.icon;

                return (
                  <Box key={type} sx={{ mb: 2 }}>
                    <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.default' }}>
                      {TypeIcon && <TypeIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
                      <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                        {typeLabels[type as keyof typeof typeLabels]?.label}
                      </Typography>
                      <Chip label={typeResults.length} size="small" sx={{ height: 18, fontSize: '0.7rem' }} />
                    </Box>
                    <List dense>
                      {typeResults.map((result) => (
                        <ListItemButton
                          key={`${result.type}-${result.id}`}
                          onClick={() => handleResultClick(result)}
                          sx={{
                            mx: 1,
                            borderRadius: 1,
                            mb: 0.5,
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            {getResultIcon(result.type)}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {result.title}
                              </Typography>
                            }
                            secondary={
                              <Box>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {result.subtitle}
                                </Typography>
                                {result.metadata && (
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                    {result.metadata}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  </Box>
                );
              })}
            </Box>
          ) : (
            // Desktop: Filtered list with tabs
            <List sx={{ py: 1 }}>
              {filteredResults.map((result, index) => (
                <ListItemButton
                  key={`${result.type}-${result.id}`}
                  selected={index === selectedIndex}
                  onClick={() => handleResultClick(result)}
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    mb: 0.5,
                    '&.Mui-selected': {
                      bgcolor: alpha('#10b981', 0.1),
                      '&:hover': {
                        bgcolor: alpha('#10b981', 0.15),
                      },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {getResultIcon(result.type)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {result.title}
                        </Typography>
                        <Chip
                          label={result.type}
                          size="small"
                          color={getResultColor(result.type) as any}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {result.subtitle}
                        </Typography>
                        {result.metadata && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            {result.metadata}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>

        {/* Keyboard Shortcuts Help - Desktop Only */}
        {!isMobile && filteredResults.length > 0 && (
          <>
            <Divider />
            <Box sx={{ p: 1.5, display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                ↑↓ Navigate
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ↵ Select
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Esc Close
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
